import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAdapter, useTopology } from "@/store/site";
import { useUiPref } from "@/store/uiPrefs";
import { useNow } from "@/hooks/useNow";
import { useT } from "@/lib/i18n";
import { carryTo } from "@/lib/carryParams";
import { autoGran } from "@/lib/timeRange";
import { formatDateTime, formatHourMin } from "@/lib/format";
import {
  availabilityFetchPlan,
  deriveAvailability,
  type AvailabilitySegment,
} from "@/lib/availability";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { ChartSkeleton } from "@/components/ui/ChartSkeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/cn";

/**
 * Per-unit availability timelines (AVAILABILITY CONTRACT).
 *
 *  - <AvailabilityRibbon target start end/> — one self-fetching horizontal
 *    ribbon: online stretches in a calm ok tone, no-data stretches as
 *    striped --ems-stale segments that LINK into the Historical analyzer
 *    pinned to the gap's forensic window.
 *  - <AvailabilityCard/> — fully self-contained fleet card: one ribbon per
 *    topology BESS unit + a grid-meter row, 24h/7d/30d window selector
 *    persisted via useUiPref("availability.window").
 *
 * Availability derives from history sample gaps (lib/availability) — the
 * concept is a horizontal-ribbon take on the sibling TB-EMS dashboard's
 * device-activity-timeline (calendar heatmap of online/offline + uptime %),
 * which suits a multi-unit fleet card better than per-unit calendars.
 */

export interface AvailabilityTarget {
  kind: "bess" | "grid";
  /** Topology BESS unit id — required for kind "bess". */
  id?: string;
}

const MIN = 60_000;
const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/**
 * Compact duration for tooltips/summaries: "42 min", "1 h 20 min", "2 d 4 h".
 * Unit abbreviations are SI-style and deliberately untranslated.
 */
function fmtDuration(ms: number): string {
  const totalMin = Math.round(ms / MIN);
  if (totalMin < 60) return `${Math.max(1, totalMin)} min`;
  const totalH = Math.floor(totalMin / 60);
  if (totalH < 48) {
    const m = totalMin % 60;
    return m > 0 ? `${totalH} h ${m} min` : `${totalH} h`;
  }
  const d = Math.floor(totalH / 24);
  const h = totalH % 24;
  return h > 0 ? `${d} d ${h} h` : `${d} d`;
}

/** "12:05" within the same calendar day, full date·time otherwise. */
function fmtGapEdge(ts: number, otherTs: number): string {
  const a = new Date(ts);
  const b = new Date(otherTs);
  const sameDay =
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  return sameDay ? formatHourMin(ts) : formatDateTime(ts);
}

/** Uptime severity per the contract: ok ≥ 99, warn ≥ 95, critical below. */
function uptimeClass(pct: number): string {
  if (pct >= 99) return "text-ok";
  if (pct >= 95) return "text-warn";
  return "text-critical";
}

function fmtUptime(pct: number): string {
  return pct >= 99.95 ? "100%" : `${pct.toFixed(1)}%`;
}

// Calm fills driven by --ems-* vars so dark mode flips for free. The gap
// stripes wear --ems-stale (data-quality grey, never an alarm colour).
const ONLINE_FILL = "color-mix(in srgb, var(--ems-ok) 30%, transparent)";
const GAP_FILL =
  "repeating-linear-gradient(135deg, " +
  "color-mix(in srgb, var(--ems-stale) 60%, transparent) 0 5px, " +
  "color-mix(in srgb, var(--ems-stale) 22%, transparent) 5px 10px)";

export function AvailabilityRibbon({
  target,
  start,
  end,
  height = 18,
}: {
  target: AvailabilityTarget;
  start: number;
  end: number;
  height?: number;
}) {
  const t = useT();
  const adapter = useAdapter();
  const topology = useTopology();

  const plan = availabilityFetchPlan(end - start);
  // Snap the query start to the bucket grid: stable cache keys + bucket
  // boundaries deterministic across renders (see availabilityFetchPlan).
  const qStart = Math.floor(start / plan.snapMs) * plan.snapMs;
  const qEnd = end;

  // bess targets fetch via bess_id + catalog key "soc" (SOC is on every
  // uplink at every cadence; tbAdapter maps it to the unit device's
  // battery_soc_%). Grid uses the curated grid_W series.
  const seriesKey = target.kind === "grid" ? "grid_W" : "soc";

  const name =
    target.kind === "grid"
      ? t("availability.gridMeter")
      : topology.bess.find((b) => b.id === target.id)?.name ?? target.id ?? "BESS";

  const { data, isError, refetch } = useQuery({
    queryKey: [
      "availability",
      topology.id,
      target.kind,
      target.id ?? "",
      qStart,
      qEnd,
      plan.gran,
    ],
    queryFn: () =>
      adapter.history({
        ...(target.kind === "bess" && target.id ? { bess_id: target.id } : {}),
        keys: [seriesKey],
        start_ts: qStart,
        end_ts: qEnd,
        granularity: plan.gran,
      }),
    staleTime: 30_000,
  });

  const derived = useMemo(() => {
    if (!data) return null;
    return deriveAvailability(data.series[seriesKey] ?? [], {
      start: qStart,
      end: qEnd,
      expectedCadenceMs: plan.expectedCadenceMs,
    });
  }, [data, seriesKey, qStart, qEnd, plan.expectedCadenceMs]);

  // Forensic deep link for one gap: the Historical analyzer pinned to the
  // gap window plus breathing room on both sides. The series carried is
  // the SAME one availability was derived from.
  const gapHref = (seg: AvailabilitySegment): string => {
    const gapDur = seg.end - seg.start;
    const pad = Math.min(2 * HOUR, Math.max(15 * MIN, gapDur));
    const winStart = seg.start - pad;
    const winEnd = seg.end + pad;
    let keys = seriesKey;
    let bessFocus: string | undefined;
    if (target.kind === "bess" && target.id) {
      const deviceKeys = adapter.historicalKeysFor(target.id, ["soc"]);
      keys = deviceKeys.length ? deviceKeys.join(",") : "battery/aggregation/SOC";
      bessFocus = target.id;
    }
    return carryTo("/historical", {
      keys,
      start: String(winStart),
      end: String(winEnd),
      gran: autoGran(winEnd - winStart),
      preset: "custom",
      ...(bessFocus ? { bess: bessFocus } : {}),
    });
  };

  const spanMs = Math.max(1, qEnd - qStart);

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="truncate text-xs font-medium text-text">{name}</span>
        {derived && (
          <span className="flex shrink-0 items-baseline gap-2 text-[11px]">
            {derived.totalGapMs > 0 && (
              <span className="text-text-muted">
                {t("availability.gapTotal", { duration: fmtDuration(derived.totalGapMs) })}
              </span>
            )}
            <span className={cn("num font-semibold", uptimeClass(derived.uptimePct))}>
              {fmtUptime(derived.uptimePct)}
            </span>
          </span>
        )}
      </div>
      {isError && data === undefined ? (
        <div
          className="flex items-center justify-between rounded-md border border-dashed border-edge bg-surface-2/40 px-2 text-[11px] text-text-muted"
          style={{ height }}
        >
          <span>{t("availability.loadError")}</span>
          <button
            onClick={() => refetch()}
            className="rounded border border-edge bg-surface px-1.5 py-0.5 text-[10px] hover:border-accent hover:text-accent"
          >
            {t("common.retry")}
          </button>
        </div>
      ) : !derived ? (
        <ChartSkeleton height={height} className="rounded-md" />
      ) : (
        <div
          role="group"
          aria-label={t("availability.ribbonAria", { name })}
          className="flex w-full gap-px overflow-hidden rounded-md border border-edge bg-surface-2/40"
          style={{ height }}
        >
          {derived.segments.map((seg) =>
            seg.state === "online" ? (
              <div
                key={seg.start}
                aria-hidden
                className="h-full"
                style={{
                  flexGrow: (seg.end - seg.start) / spanMs,
                  flexBasis: 0,
                  background: ONLINE_FILL,
                }}
              />
            ) : (
              <Link
                key={seg.start}
                to={gapHref(seg)}
                title={t("availability.gapTitle", {
                  from: fmtGapEdge(seg.start, seg.end),
                  to: fmtGapEdge(seg.end, seg.start),
                  duration: fmtDuration(seg.end - seg.start),
                })}
                aria-label={t("availability.gapTitle", {
                  from: fmtGapEdge(seg.start, seg.end),
                  to: fmtGapEdge(seg.end, seg.start),
                  duration: fmtDuration(seg.end - seg.start),
                })}
                className="h-full transition-opacity hover:opacity-75"
                style={{
                  flexGrow: (seg.end - seg.start) / spanMs,
                  flexBasis: 0,
                  minWidth: 6,
                  background: GAP_FILL,
                }}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Fleet card                                                                 */
/* -------------------------------------------------------------------------- */

type AvailabilityWindowKey = "24h" | "7d" | "30d";

// Chip labels are numeric ("24 h") — deliberately not translated.
const WINDOWS: Array<{ key: AvailabilityWindowKey; label: string; ms: number }> = [
  { key: "24h", label: "24 h", ms: DAY },
  { key: "7d", label: "7 d", ms: 7 * DAY },
  { key: "30d", label: "30 d", ms: 30 * DAY },
];

export function AvailabilityCard() {
  const t = useT();
  const topology = useTopology();
  const [winPref, setWinPref] = useUiPref<AvailabilityWindowKey>("availability.window", "7d");
  // Re-validate the stored pref (uiPrefs contract) — unknown values fall
  // back to the 7d default instead of producing a zero-width window.
  const win = WINDOWS.find((w) => w.key === winPref) ?? WINDOWS[1];

  // Minute-snapped "now": stable query keys within the minute, and the
  // useNow tick advances the window (one refetch per row per minute).
  const now = useNow(60_000);
  const end = Math.floor(now / MIN) * MIN;
  const start = end - win.ms;

  const hasGrid = topology.smartmeters.some((m) => m.role === "grid");
  const hasTargets = topology.bess.length > 0 || hasGrid;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("availability.title")}</CardTitle>
        <div
          role="group"
          aria-label={t("availability.windowAria")}
          className="flex items-center gap-1 rounded-md border border-edge bg-surface p-0.5 text-[11px]"
        >
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWinPref(w.key)}
              className={cn(
                "rounded px-2 py-1",
                win.key === w.key ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text",
              )}
            >
              {w.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <div className="space-y-3 p-4">
        <p className="text-[11px] text-text-muted">{t("availability.explainer")}</p>
        {!hasTargets ? (
          <EmptyState
            title={t("availability.empty.title")}
            detail={t("availability.empty.detail")}
            height={160}
          />
        ) : (
          <>
            {topology.bess.map((b) => (
              <AvailabilityRibbon
                key={`${b.id}:${win.key}`}
                target={{ kind: "bess", id: b.id }}
                start={start}
                end={end}
              />
            ))}
            {hasGrid && (
              <AvailabilityRibbon
                key={`grid:${win.key}`}
                target={{ kind: "grid" }}
                start={start}
                end={end}
              />
            )}
          </>
        )}
      </div>
    </Card>
  );
}
